import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { RechnungStatusLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import { Rechnung } from 'api/rechnung';
import { Timestamps } from 'formComponents/Timestamps';
import { RechnungPosition } from 'api/rechnungPosition';
import {
    araRegelungenByArtSelector,
    betreuerByNrSelector,
    laenderByIkz2Selector,
    skontodefinitionenByArtSelector,
    textbausteineByKzNrSelector,
} from 'store/lookups/selector';
import { formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { authSelector } from 'store/auth/selector';
import nl2br from 'react-nl2br';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { RechnungPositionen } from './RechnungPositionen';

const styles = mergeStyleSets({
    shortLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '120px 120px 1fr 100px max-content',
        gridColumnGap: 10,
    },
    status: {
        textAlign: 'right',
    },
});

interface RechnungItemProps {
    rechnung: Rechnung;
    rechnungPositionen: RechnungPosition[];
    withKunde?: boolean;
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    editRechnung: () => void;
    // refetch: () => void;
}

export const RechnungItem: React.FC<RechnungItemProps> = ({
    rechnung,
    rechnungPositionen,
    withKunde,
    viewMode,
    withHeader = true,
    editRechnung,
}) => {
    const { kunde } = rechnung;

    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const textbausteineByKzNr = useSelector(textbausteineByKzNrSelector);
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);
    const araRegelungenByArt = useSelector(araRegelungenByArtSelector);
    const laenderByIkz2 = useSelector(laenderByIkz2Selector);
    const history = useHistory();

    if (!kunde) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={rechnung.anlageDat}
            createdDecimalTime={rechnung.anlageUhr}
            createdUser={rechnung.anlageUser}
            modifiedDate={rechnung.updDat}
            modifiedDecimalTime={rechnung.updUhr}
            modifiedUser={rechnung.updUser}
        />
    );

    const renderStatus = () => <div className={styles.status}>{RechnungStatusLabel[rechnung.status]}</div>;

    const renderEditButton = () => (
        <div>
            {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editRechnung();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const containerClassName = classnames(viewMode === 'short' ? formStyles.listItemShort : formStyles.listItem, {
        [formStyles.rechnungInArbeit]: rechnung.status === 0,
        [formStyles.rechnungVersendet]: rechnung.status === 1 || rechnung.status === 5,
        [formStyles.rechnungBezahlt]: rechnung.status === 6,
    });

    const rechnungGutschrift = (rechnung?.sNetto || 0) < 0 ? 'Gutschrift' : 'Rechnung';
    const rechnungGutschriftShort = (rechnung?.sNetto || 0) < 0 ? 'GS' : 'RN';
    const teilrechnung = rechnung?.trProz !== 0 ? '(T) ' : '';

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                <div className={styles.shortLine}>
                    <div className={formStyles.shortValue}>
                        <strong>
                            {rechnungGutschriftShort} {rechnung.nr} {teilrechnung}
                        </strong>
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{formatSmDate(rechnung.datum)}</strong>
                    </div>
                    {withKunde && (
                        <div
                            className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                            }}
                        >
                            {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    )}

                    {renderStatus()}
                    {renderEditButton()}
                </div>

                <div>
                    <RechnungPositionen
                        kunde={kunde}
                        rNr={rechnung.nr}
                        rechnungPositionen={rechnungPositionen}
                        viewMode={viewMode}
                        withHeader={withHeader}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={containerClassName}>
                <div className={formStyles.topBlock}>
                    <div>
                        <div
                            className={classnames(formStyles.listTitle, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                                // openDetails();
                            }}
                        >
                            {rechnungGutschrift} {rechnung.nr} {teilrechnung}-{' '}
                            {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.line}>
                            {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ansprechpartner:</div>
                            <div className={formStyles.dataValue}>{rechnung.kaName}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Rechnung-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{rechnung.nr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{rechnung.auNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Rahmenauftrag:</div>
                            <div className={formStyles.dataValue}>{rechnung.rahmen ? 'Ja' : 'Nein'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ausstellungsdat.:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(rechnung.datum)}</strong>
                            </div>
                        </div>
                        {/*
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag gültig bis:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(rechnung.datBis)}</strong>
                            </div>
                        </div>
                        */}
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {rechnung.betreuer &&
                                        (betreuerByNr[rechnung.betreuer]
                                            ? betreuerByNr[rechnung.betreuer].name || rechnung.betreuer
                                            : rechnung.betreuer)}
                                </strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Tel.-Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {rechnung.telBetreuer
                                        ? betreuerByNr[rechnung.telBetreuer]
                                            ? betreuerByNr[rechnung.telBetreuer].name || rechnung.telBetreuer
                                            : rechnung.telBetreuer
                                        : '-'}
                                </strong>
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Text:</div>
                            <div className={formStyles.dataValue}>{nl2br(rechnung.txt)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 1:</div>
                            <div className={formStyles.dataValue}>
                                {rechnung.txtB1
                                    ? textbausteineByKzNr[25][rechnung.txtB1]
                                        ? textbausteineByKzNr[25][rechnung.txtB1].name || rechnung.txtB1
                                        : rechnung.txtB1
                                    : '-'}
                            </div>
                        </div>
                        {rechnung.lieferAdr && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Lieferadresse:</div>
                                <div className={formStyles.dataValue}>
                                    {`${rechnung.lieferAdr.name1} ${rechnung.lieferAdr.name2}`}
                                    <br />
                                    {rechnung.lieferAdr.plz} {rechnung.lieferAdr.ort}
                                    {', '}
                                    {rechnung.lieferAdr.strasse}
                                    {rechnung.lieferAdr.land &&
                                        ', ' +
                                            (rechnung.lieferAdr.land
                                                ? laenderByIkz2[rechnung.lieferAdr.land]?.text
                                                : '')}
                                </div>
                            </div>
                        )}
                        {rechnung.rechnungsAdr && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Rechnungsadresse:</div>
                                <div className={formStyles.dataValue}>
                                    {`${rechnung.rechnungsAdr.name1} ${rechnung.rechnungsAdr.name2}`}
                                    <br />
                                    {rechnung.rechnungsAdr.plz} {rechnung.rechnungsAdr.ort}
                                    {', '}
                                    {rechnung.rechnungsAdr.strasse}
                                    {rechnung.rechnungsAdr.land &&
                                        ', ' +
                                            (rechnung.rechnungsAdr.land
                                                ? laenderByIkz2[rechnung.rechnungsAdr.land]?.text
                                                : '')}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferbed.:</div>
                            <div className={formStyles.dataValue}>
                                {rechnung.liefBed
                                    ? textbausteineByKzNr[11][rechnung.liefBed]
                                        ? textbausteineByKzNr[11][rechnung.liefBed].name || rechnung.liefBed
                                        : rechnung.liefBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferzeit:</div>
                            <div className={formStyles.dataValue}>
                                {rechnung.liefZeit
                                    ? textbausteineByKzNr[14][rechnung.liefZeit]
                                        ? textbausteineByKzNr[14][rechnung.liefZeit].name || rechnung.liefZeit
                                        : rechnung.liefZeit
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferdatum.:</div>
                            <div className={formStyles.dataValue}>
                                {rechnung.liefDat && (formatSmDate(rechnung.liefDat, true) || '-')}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Liefer-KW.:</div>
                            <div className={formStyles.dataValue}>{rechnung.liefKw ? rechnung.liefKw : '-'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zahlungsbed.:</div>
                            <div className={formStyles.dataValue}>
                                {rechnung.zahlBed
                                    ? skontodefinitionenByArt[rechnung.zahlBed]
                                        ? skontodefinitionenByArt[rechnung.zahlBed].text || rechnung.zahlBed
                                        : rechnung.zahlBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>ARA Regelung:</div>
                            <div className={formStyles.dataValue}>
                                {rechnung.ara
                                    ? araRegelungenByArt[rechnung.ara]
                                        ? araRegelungenByArt[rechnung.ara].text || rechnung.ara
                                        : rechnung.ara
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Fracht Lager:</div>
                            <div className={formStyles.dataValue}>{formatMoney(rechnung.fracht)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Fracht extern:</div>
                            <div className={formStyles.dataValue}>{formatMoney(rechnung.roadPr)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Tipp-Prof.:</div>
                            <div className={formStyles.dataValue}>{formatPercent(rechnung.tipp)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>MwSt:</div>
                            <div className={formStyles.dataValue}>{formatPercent(rechnung.mwstCode)}</div>
                        </div>
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Bemerkungen (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(rechnung?.kunde?.bemerkung)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Lieferbemerkung (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(rechnung?.kunde?.liefBem)}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <RechnungPositionen
                        kunde={kunde}
                        rNr={rechnung.nr}
                        rechnungPositionen={rechnungPositionen}
                        viewMode={viewMode}
                    />
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.sumLine}>Summe Netto: {formatMoney(rechnung.sNetto)}</div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.sumLine}>Summe Brutto: {formatMoney(rechnung.sBrutto)}</div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.sumLine}>Summe MwSt.: {formatMoney(rechnung.sMwst)}</div>
                    </div>
                </div>
                {renderTimestamps()}
            </div>
            {/* showCopy && (
                <KundeSelect
                    label="Kunde"
                    kNr={rechnung?.kNr}
                    name={kunde?.name1}
                    dialogOnly
                    onChange={async (newKNr) => {
                        if (rechnung && newKNr) {
                            await copyRechnung(newKNr, rechnung, rechnungPositionen);
                            refetch();
                            setShowCopy(false);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            ) */}
        </>
    );
};
